import apiClient from './api';
import { API_ENDPOINTS } from '@/config/api.config';
import type {
  ForumDiscussionDto,
  CreateDiscussionDto,
  UpdateDiscussionDto,
  GetDiscussionsDto,
  DeleteDiscussionDto,
  LikeDiscussionDto,
  ForumDiscussionListResponseDto,
  CreateDiscussionResponse,
  UpdateDiscussionResponse,
  DeleteDiscussionResponse,
  DiscussionListResponse,
  ReactDiscussionResponse,
} from '@/types/discussion.types';

/**
 * Service for managing forum discussions (comments)
 * Provides nested discussion functionality like YouTube/Instagram
 */
class DiscussionService {
  /**
   * Create a new discussion (top-level or reply)
   * @param data - Discussion creation data
   * @returns Promise with created discussion
   */
  async createDiscussion(data: CreateDiscussionDto): Promise<ForumDiscussionDto> {
    try {
      const response = await apiClient.post<CreateDiscussionResponse>(
        API_ENDPOINTS.FORUM.CREATE_DISCUSSION,
        data
      );

      if (!response.data.success) {
        throw new Error(response.data.description || 'Failed to create discussion');
      }

      return response.data.data;
    } catch (error) {
      console.error('Error creating discussion:', error);
      throw error;
    }
  }

  /**
   * Get paginated list of discussions with optional filtering
   * @param params - Query parameters for filtering and pagination
   * @returns Promise with paginated discussion list
   */
  async getDiscussions(params: GetDiscussionsDto): Promise<ForumDiscussionListResponseDto> {
    try {

      const response = await apiClient.post<DiscussionListResponse>(
        API_ENDPOINTS.FORUM.LIST_DISCUSSIONS,
        params
      );

      if (!response.data.success) {
        throw new Error(response.data.description || 'Failed to fetch discussions');
      }

      const result = response.data.data;
      return result;
    } catch (error: any) {
      throw error;
    }
  }

  /**
   * Update an existing discussion
   * @param data - Update data including thread_id and discussion_id
   * @returns Promise with updated discussion
   */
  async updateDiscussion(data: UpdateDiscussionDto): Promise<ForumDiscussionDto> {
    try {
      const response = await apiClient.patch<UpdateDiscussionResponse>(
        API_ENDPOINTS.FORUM.UPDATE_DISCUSSION,
        data
      );

      if (!response.data.success) {
        throw new Error(response.data.description || 'Failed to update discussion');
      }

      return response.data.data;
    } catch (error) {
     
      throw error;
    }
  }

  /**
   * Delete a discussion (soft delete)
   * @param data - Deletion data including thread_id and discussion_id
   * @returns Promise with deletion result
   */
  async deleteDiscussion(data: DeleteDiscussionDto): Promise<{ deleted: boolean; discussion_id: string }> {
    try {
      const response = await apiClient.delete<DeleteDiscussionResponse>(
        API_ENDPOINTS.FORUM.DELETE_DISCUSSION,
        { data }
      );

      if (!response.data.success) {
        throw new Error(response.data.description || 'Failed to delete discussion');
      }

      return response.data.data;
    } catch (error) {
    
      throw error;
    }
  }

  /**
   * React to a discussion (like/unlike/dislike/undislike)
   * @param data - Reaction data
   * @returns Promise with updated discussion
   */
  async reactToDiscussion(data: LikeDiscussionDto): Promise<ForumDiscussionDto> {
    try {
      const response = await apiClient.post<ReactDiscussionResponse>(
        API_ENDPOINTS.FORUM.REACT_DISCUSSION,
        data
      );

      if (!response.data.success) {
        throw new Error(response.data.description || 'Failed to react to discussion');
      }

      return response.data.data;
    } catch (error) {
      console.error('Error reacting to discussion:', error);
      throw error;
    }
  }

  /**
   * Get top-level discussions for a thread
   * @param threadId - The thread ID
   * @param page - Page number
   * @param limit - Items per page
   * @param sortBy - Sort order
   * @returns Promise with top-level discussions
   */
  async getTopLevelDiscussions(
    threadId: string,
    page: number = 1,
    limit: number = 10,
    sortBy: 'oldest' | 'newest' | 'most_liked' = 'newest'
  ): Promise<ForumDiscussionListResponseDto> {
    return this.getDiscussions({
      thread_id: threadId,
      parent_id: null, // null for top-level
      page,
      limit,
      sort_by: sortBy,
    });
  }

  /**
   * Get replies for a specific discussion
   * @param threadId - The thread ID
   * @param parentId - The parent discussion ID
   * @param page - Page number
   * @param limit - Items per page
   * @returns Promise with reply discussions
   */
  async getReplies(
    threadId: string,
    parentId: string,
    page: number = 1,
    limit: number = 5
  ): Promise<ForumDiscussionListResponseDto> {
    return this.getDiscussions({
      thread_id: threadId,
      parent_id: parentId,
      page,
      limit,
      sort_by: 'oldest', // Replies typically shown oldest first
    });
  }

  /**
   * Like a discussion
   * @param threadId - Thread ID
   * @param discussionId - Discussion ID
   * @returns Promise with updated discussion
   */
  async likeDiscussion(threadId: string, discussionId: string): Promise<ForumDiscussionDto> {
    return this.reactToDiscussion({
      thread_id: threadId,
      discussion_id: discussionId,
      action: 'like',
    });
  }

  /**
   * Unlike a discussion
   * @param threadId - Thread ID
   * @param discussionId - Discussion ID
   * @returns Promise with updated discussion
   */
  async unlikeDiscussion(threadId: string, discussionId: string): Promise<ForumDiscussionDto> {
    return this.reactToDiscussion({
      thread_id: threadId,
      discussion_id: discussionId,
      action: 'unlike',
    });
  }

  /**
   * Dislike a discussion
   * @param threadId - Thread ID
   * @param discussionId - Discussion ID
   * @returns Promise with updated discussion
   */
  async dislikeDiscussion(threadId: string, discussionId: string): Promise<ForumDiscussionDto> {
    return this.reactToDiscussion({
      thread_id: threadId,
      discussion_id: discussionId,
      action: 'dislike',
    });
  }

  /**
   * Undislike a discussion
   * @param threadId - Thread ID
   * @param discussionId - Discussion ID
   * @returns Promise with updated discussion
   */
  async undislikeDiscussion(threadId: string, discussionId: string): Promise<ForumDiscussionDto> {
    return this.reactToDiscussion({
      thread_id: threadId,
      discussion_id: discussionId,
      action: 'undislike',
    });
  }
}

// Export singleton instance
export const discussionService = new DiscussionService();
